import { createEvent, createStore, restore, sample } from 'effector'
import { fx, path } from '../../../libs'
import { Verification, VerificationApprove, Error } from './types'
import { toPath } from '../../../features/router'
import { verificationStatus } from '../const'
import { rejectVerification } from '../atoms/rejectModal/model'
import { VerificationResponse } from '../list/types'
import { getVerificationRequests } from '../atoms/duplicates/model'
import {
  setRequestSuccess,
  setRequestError,
} from '../../../features/notifications'

export const verificationRequestMounted = createEvent()
export const verificationRequestUnmounted = createEvent()
export const $isFlowMounted = createStore(false)
  .on(verificationRequestMounted, () => true)
  .on(verificationRequestUnmounted, () => false)

export type FlowFilters = {
  status?: string[]
  country?: string[]
  hasDeposit?: boolean
  isDuplicate?: boolean
}
export const setFlowFilters = createEvent<FlowFilters>()
export const $flowFilters = restore<FlowFilters>(setFlowFilters, {})

const buildWhere = (filters: FlowFilters): Record<string, unknown> => {
  const where: Record<string, unknown> = {}
  where.status = filters.status?.length
    ? filters.status
    : [verificationStatus.verificationRequestStatusNew()]
  if (filters.country?.length) where.country = filters.country
  if (filters.hasDeposit !== undefined)
    where['client.hasDeposit'] = String(filters.hasDeposit)
  if (filters.isDuplicate !== undefined)
    where.isDuplicate = String(filters.isDuplicate)
  return where
}

const buildGetVerificationQuery = (filters: FlowFilters) => ({
  query: { limit: '1', where: JSON.stringify(buildWhere(filters)) },
})

export const setVerificationRequest = createEvent<Verification>()
export const submitApprove = createEvent<VerificationApprove>()
export const submitEditVerificationRequest = createEvent<Verification>()
export const submitSkip = createEvent<string>()
export const submitExit = createEvent()
export const setIsFormDirtyFlow = createEvent<boolean>()
export const $isFormDirtyFlow = restore(setIsFormDirtyFlow, false)

export type FormDirtyStateFlow = {
  dirtyFieldKeys: string[]
  expirationDate: string
  values: Pick<
    Verification,
    | 'firstName'
    | 'middleName'
    | 'lastName'
    | 'country'
    | 'dateOfBirth'
    | 'documentNumber'
    | 'expirationDate'
  >
}
export const setFormDirtyStateFlow = createEvent<FormDirtyStateFlow>()
export const $formDirtyStateFlow = restore<FormDirtyStateFlow>(
  setFormDirtyStateFlow,
  {
    dirtyFieldKeys: [],
    expirationDate: '',
    values: {
      firstName: '', middleName: '', lastName: '', country: '', dateOfBirth: '', documentNumber: '', expirationDate: '',
    },
  },
)
export const $verificationRequest = restore(setVerificationRequest, {
  requestId: '',
  clientId: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  status: '',
  country: '',
  city: '',
  address: '',
  zip: '',
  documentType: '',
  documentNumber: '',
  fileNames: [],
  browserFileNames: [],
  exampleFiles: '',
  expirationDate: '',
  isDuplicate: false,
  hasDeposit: false,
  profileNotes: '',
  comment: '',
  fullName: '',
  reason: '',
  manager: {
    id: '',
    user: {
      email: '',
      fullName: '',
      fullname: '',
    },
  },
  client: {
    clientId: '',
    email: '',
    fullName: '',
    partner: false,
    verified: false,
    hasDeposit: false,
    isPremium: false,
    isTest: false,
  },
  totalBalanceUSD: 0,
})

export const getVerification = fx<void, VerificationResponse>({
  method: 'GET',
  url: `/verification/flow`,
})

type VerificationStatusCommandBody = Record<string, never>
type VerificationDataUpdateBody = Omit<
  Verification,
  'status' | 'reason' | 'comment' | 'requestId' | 'clientId' | 'manager' | 'client'
>

export const approveVerification = fx<VerificationStatusCommandBody, Verification>({
  method: 'POST',
  url: `/verification/`,
})

export const saveVerification = fx<VerificationDataUpdateBody, Verification>({
  method: 'PUT',
  url: `/verification/`,
})

export const submitSaveVerificationFields = createEvent<Verification>()

export const saveVerificationFields = fx<VerificationDataUpdateBody, Verification>({
  method: 'PUT',
  url: `/verification/`,
})

export const setError = createEvent<Error>()
export const $error = restore(setError, {
  message: '',
  code: 0,
})

sample({
  clock: submitApprove,
  fn: (verification) => {
    return {
      body: {},
      url: `/verification/${verification.requestId}/accept`,
    }
  },
  target: approveVerification,
})

sample({
  clock: submitEditVerificationRequest,
  fn: (verification) => {
    return {
      body: (({ status, reason, comment, requestId, clientId, manager, client, ...fields }) => fields)(verification),
      url: `/verification/${verification.requestId}`,
    }
  },
  target: saveVerification,
})

sample({
  clock: submitSaveVerificationFields,
  fn: (verification) => {
    return {
      body: (({ status, reason, comment, requestId, clientId, manager, client, ...fields }) => fields)(verification),
      url: `/verification/${verification.requestId}`,
    }
  },
  target: saveVerificationFields,
})

sample({
  clock: saveVerificationFields.doneData,
  fn: (doneData) => doneData.body,
  target: setVerificationRequest,
})

sample({
  clock: saveVerificationFields.doneData,
  fn: () => true,
  target: setRequestSuccess,
})

sample({
  clock: saveVerificationFields.failData,
  fn: (failData) => ({
    message: failData.body.message,
    code: failData.body.code,
  }),
  target: setRequestError,
})

sample({
  source: $flowFilters,
  clock: verificationRequestMounted,
  fn: buildGetVerificationQuery,
  target: getVerification,
})

sample({
  source: $flowFilters,
  clock: $flowFilters.updates,
  filter: $isFlowMounted,
  fn: buildGetVerificationQuery,
  target: getVerification,
})

// update profile model

sample({
  // @ts-ignore
  clock: getVerification.doneData,
  fn: (doneData) => doneData.body.items[0],
  target: setVerificationRequest,
})

sample({
  clock: approveVerification.doneData,
  fn: () => true,
  target: setRequestSuccess,
})

sample({
  clock: approveVerification.failData,
  fn: (failData) => ({
    message: failData.body.message,
    code: failData.body.code,
  }),
  target: setRequestError,
})

sample({
  source: $flowFilters,
  clock: approveVerification.doneData,
  fn: buildGetVerificationQuery,
  target: getVerification,
})

sample({
  source: $flowFilters,
  clock: rejectVerification.doneData,
  filter: $isFlowMounted,
  fn: buildGetVerificationQuery,
  target: getVerification,
})

sample({
  clock: [saveVerification.failData],
  fn: (failData) => {
    const message = JSON.parse(failData.body.message)

    return {
      url: `/verification/${message?.requestId}/duplicates`,
    }
  },
  target: [getVerificationRequests],
})

sample({
  clock: [saveVerification.failData],
  fn: (failData) => {
    return { message: failData.body.message, code: failData.body.code }
  },
  target: [setError],
})
sample({
  clock: [saveVerification.doneData],
  fn: (doneData) => {
    return doneData.body
  },
  target: [submitApprove],
})

const skipLeave = fx<void, void>({
  method: 'DELETE',
  url: `/verification/flow`,
})

const exitLeave = fx<void, void>({
  method: 'DELETE',
  url: `/verification/flow`,
})

sample({
  clock: submitSkip,
  fn: (requestId: string) => ({
    url: `/verification/flow/${requestId}`,
  }),
  target: skipLeave,
})

sample({
  clock: submitExit,
  fn: () => ({}),
  target: exitLeave,
})

sample({
  source: $flowFilters,
  clock: skipLeave.doneData,
  fn: buildGetVerificationQuery,
  target: getVerification,
})

sample({
  clock: exitLeave.doneData,
  target: toPath.prepend(path.verificationsV2.entry),
})
